import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useProgress } from "../contexts/ProgressContext";
import Header from "../components/Header";
import LanguageToggle from "../components/LanguageToggle";
import ProgressRing from "../components/ProgressRing";
import StudyPlan from "../components/StudyPlan";
import StudentProgressCharts from "../components/StudentProgressCharts";
import MyAssignments from "../components/MyAssignments";
import MyResources from "../components/MyResources";
import WhatsNewBanner from "../components/WhatsNewBanner";
import { Stethoscope, MessagesSquare, Brain, Heart, ClipboardList, Activity, ArrowRight, BookOpenText, Award, Target, Sparkles, Database } from "lucide-react";

const ICON_MAP = { Stethoscope, MessagesSquare, Brain, Heart, ClipboardList, Activity };

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t, isRTL, lang } = useLanguage();
  const { chapterSummary, views, quizBest, quizHistory } = useProgress();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/");
    else if (!loading && user && user.status === "pending") navigate("/pending", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/chapters").then((r) => setChapters(r.data)).catch((e) => setErr("Failed to load chapters"));
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone text-sage">
        {t("loading")}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div className="animate-fade-up">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
              {t("welcomeBack")}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              {user.name?.split(" ")[0]}.
            </h1>
            <p className="mt-3 text-muted-foreground max-w-xl">
              {t("tagline")}
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-4 py-2 text-sm">
            <BookOpenText className="w-4 h-4 text-sage" strokeWidth={1.8} />
            <span className="text-muted-foreground">{t("yourCourse")}:</span>
            <span className="font-medium">Fundamentals of Nursing</span>
          </div>
        </div>

        {err && <div className="text-terracotta text-sm mb-4">{err}</div>}

        {/* What's new — latest chapter + group resources */}
        <WhatsNewBanner lang={lang} />

        {/* My Assignments — teacher-assigned tasks (quizzes, homework, flashcard reviews) */}
        <MyAssignments lang={lang} />

        {/* My Resources — teacher-provided resources */}
        <MyResources lang={lang} />

        {/* Daily study plan */}
        <div className="mb-8">
          <StudyPlan chapters={chapters} />
        </div>

        {/* Exam review pack entry */}
        <div className="mb-8 grid sm:grid-cols-2 gap-4">
        <Link
          to="/review"
          data-testid="open-review-pack"
          className="group relative block rounded-2xl border border-sage/30 bg-gradient-to-br from-sage/5 via-white to-terracotta/5 overflow-hidden p-5 sm:p-6 hover:shadow-md transition-all animate-fade-up"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-sage text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
              <Sparkles className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage font-bold mb-1">
                {lang === "ar" ? "جديد" : "New"}
              </div>
              <div className="font-display text-lg sm:text-xl font-semibold tracking-tight">
                {lang === "ar" ? "حزمة المراجعة للامتحان" : "Exam review pack"}
              </div>
              <div className="text-sm text-muted-foreground mt-1 leading-relaxed">
                {lang === "ar"
                  ? "كل الفصول — الملخصات، الرسومات، البطاقات والنقاط الإكلينيكية — في صفحة واحدة قابلة للطباعة."
                  : "Every chapter's summary, diagram, key flashcards and scenario takeaways on one printable page."}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-sage mt-3 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform shrink-0" />
          </div>
        </Link>

        <Link
          to="/questions"
          data-testid="open-questions-databank"
          className="group relative block rounded-2xl border border-terracotta/30 bg-gradient-to-br from-terracotta/5 via-white to-sage/5 overflow-hidden p-5 sm:p-6 hover:shadow-md transition-all animate-fade-up"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-terracotta text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
              <Database className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-terracotta font-bold mb-1">
                {lang === "ar" ? "بنك الأسئلة" : "Question Bank"}
              </div>
              <div className="font-display text-lg sm:text-xl font-semibold tracking-tight">
                {lang === "ar" ? "بنك الأسئلة — 500+ سؤال" : "Questions Databank · 500+"}
              </div>
              <div className="text-sm text-muted-foreground mt-1 leading-relaxed">
                {lang === "ar"
                  ? "تدرّب على أسئلة حقيقية مرتبة حسب الفصل والنوع."
                  : "Real exam questions from the EHA bank, filtered by chapter and type."}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-terracotta mt-3 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform shrink-0" />
          </div>
        </Link>
        </div>

        {/* Progress stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8 animate-fade-up" data-testid="progress-stats">
          <StatCard
            icon={Target}
            label={lang === "ar" ? "فصول مطّلعة" : "Chapters opened"}
            value={`${new Set(views.map((v) => v.chapter_id)).size} / ${chapters.length || 6}`}
          />
          <StatCard
            icon={BookOpenText}
            label={lang === "ar" ? "عناصر قُرأت" : "Items viewed"}
            value={views.length}
          />
          <StatCard
            icon={Award}
            label={lang === "ar" ? "متوسط الاختبار" : "Avg. quiz score"}
            value={quizBest.length ? `${Math.round(quizBest.reduce((s, b) => s + b.percentage, 0) / quizBest.length)}%` : "—"}
            accent
          />
        </div>

        {/* Progress charts */}
        <div className="mb-8 animate-fade-up">
          <StudentProgressCharts
            chapters={chapters}
            views={views}
            quizBest={quizBest}
            quizHistory={quizHistory}
            lang={lang}
          />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="chapters-grid">
          {chapters.map((ch, i) => {
            const Icon = ICON_MAP[ch.icon] || BookOpenText;
            const summary = chapterSummary(ch.id);
            return (
              <Link
                key={ch.id}
                to={`/chapter/${ch.id}`}
                data-testid={`chapter-card-${ch.id}`}
                className="group relative rounded-2xl bg-white border border-border p-6 transition-all hover:-translate-y-1 hover:shadow-lg animate-fade-up overflow-hidden"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${ch.gradient} opacity-0 group-hover:opacity-[0.04] transition-opacity`} aria-hidden />
                <div className="flex items-start justify-between mb-5">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${ch.gradient} flex items-center justify-center text-white shadow-sm`}>
                    <Icon className="w-6 h-6" strokeWidth={1.7} />
                  </div>
                  <div className="flex items-center gap-2">
                    {summary.quizBest && (
                      <span
                        className="text-[10px] font-bold px-2 py-1 rounded-full bg-sage/10 text-sage flex items-center gap-1"
                        data-testid={`chapter-quiz-best-${ch.id}`}
                      >
                        <Award className="w-3 h-3" strokeWidth={2.2} />
                        {summary.quizBest.percentage}%
                      </span>
                    )}
                    <ProgressRing percent={summary.percentage} size={36} stroke={3}>
                      <span className="text-sage" data-testid={`chapter-progress-${ch.id}`}>{summary.viewedCount}/{summary.totalKinds}</span>
                    </ProgressRing>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                  {t("chapter")} 0{ch.number}
                </div>
                <h3 className="font-display text-xl font-semibold tracking-tight leading-snug">
                  {ch.title}
                </h3>
                <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                  {ch.description}
                </p>
                <div className="mt-6 pt-5 border-t border-border flex items-center justify-between">
                  <div className="flex flex-wrap gap-1.5">
                    {ch.key_topics.slice(0, 2).map((k) => (
                      <span key={k} className="text-[11px] uppercase tracking-wider bg-secondary text-secondary-foreground px-2 py-1 rounded-md">
                        {k.length > 20 ? k.slice(0, 18) + "…" : k}
                      </span>
                    ))}
                    {ch.key_topics.length > 2 && (
                      <span className="text-[11px] text-muted-foreground self-center">
                        +{ch.key_topics.length - 2}
                      </span>
                    )}
                  </div>
                  <ArrowRight className={`w-5 h-5 text-sage ${isRTL ? "rotate-180" : ""} transition-transform group-hover:translate-x-1`} strokeWidth={1.8} />
                </div>
              </Link>
            );
          })}
        </div>
      </main>
      <LanguageToggle />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-5 flex items-center gap-4 ${accent ? "bg-gradient-to-br from-sage/8 to-terracotta/5 border-sage/20" : "bg-white border-border"}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent ? "bg-sage text-white" : "bg-sage/10 text-sage"}`}>
        <Icon className="w-5 h-5" strokeWidth={1.7} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{label}</div>
        <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
      </div>
    </div>
  );
}
